import { cn } from '@my/ui';
import type { LucideIcon } from 'lucide-react';

export interface KpiCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  /** Tailwind classes for the icon background tile. */
  iconBg: string;
  /** Tailwind text-color class for the icon glyph. */
  iconColor: string;
  /** Render in amber when `value > 0` (e.g. "Unassigned"). */
  alert?: boolean;
  /** Render in red when `value > 0` (e.g. "Missing POD"). */
  danger?: boolean;
}

/**
 * Compact KPI tile for the dashboard. Lifted from My.Tms.Old's inline
 * helper, rewritten to use `cn()` and explicit prop typing.
 */
export function KpiCard({
  icon: Icon,
  label,
  value,
  iconBg,
  iconColor,
  alert = false,
  danger = false,
}: KpiCardProps) {
  const isAmber = alert && value > 0;
  const isRed = danger && value > 0;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border p-3',
        isRed
          ? 'border-red-200 bg-red-50'
          : isAmber
            ? 'border-amber-200 bg-amber-50'
            : 'border-slate-200 bg-white',
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
          isRed ? 'bg-red-100' : isAmber ? 'bg-amber-100' : iconBg,
        )}
      >
        <Icon
          className={cn('h-5 w-5', isRed ? 'text-red-500' : isAmber ? 'text-amber-600' : iconColor)}
        />
      </div>
      <div>
        <div
          className={cn(
            'text-2xl font-bold leading-none',
            isRed ? 'text-red-600' : isAmber ? 'text-amber-700' : 'text-slate-800',
          )}
        >
          {value}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}
